import { useEffect, useRef } from 'react';

// ESC CHIUDE LA FINESTRA IN PRIMO PIANO, e solo quella. Dialog e drawer si mettono in pila quando
// si aprono: a ogni pressione risponde soltanto l'ultima arrivata, così una conferma aperta sopra
// un'altra finestra si chiude senza portarsi dietro quella sotto. Anche chi Esc non lo ascolta sta
// nella pila — una finestra che non si può chiudere protegge quelle che ha sotto.
//
// UN TASTO GIÀ USATO NON SI USA DUE VOLTE: un menu a tendina o una select dentro la finestra
// chiudono se stessi e segnano l'evento (`preventDefault`), e la finestra lo lascia passare.
//
// L'AMBITO: le pagine che vogliono il gesto lo accendono con `useEscapeClosesModals` — oggi il
// Composer e lo Scene Editor. Una finestra può anche deciderlo da sé: `true` chiude sempre,
// `false` mai; senza dirlo segue l'ambito.

const stack: symbol[] = [];
let scopes = 0;

/** Accende Esc per tutte le finestre aperte mentre la pagina che lo chiama è montata. */
export const useEscapeClosesModals = () => {
    useEffect(() => {
        scopes += 1;
        return () => {
            scopes -= 1;
        };
    }, []);
};

/**
 * @param onClose chi chiude la finestra
 * @param closeOnEscape `true` sempre, `false` mai, `undefined` segue l'ambito
 * @param isOpen per chi resta montato anche da chiuso, come il drawer
 */
export const useEscapeToClose = (onClose: () => void, closeOnEscape?: boolean, isOpen = true) => {
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!isOpen) return;

        const token = Symbol('modal');
        stack.push(token);

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            if (stack[stack.length - 1] !== token) return;
            if (closeOnEscape === false || (closeOnEscape === undefined && scopes === 0)) return;

            event.preventDefault();
            onCloseRef.current();
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            const index = stack.lastIndexOf(token);
            if (index >= 0) stack.splice(index, 1);
        };
    }, [closeOnEscape, isOpen]);
};
